import * as React from 'react'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import {
    AVATAR_COLORS,
    PROPERTY_HOME,
    PROPERTY_PARKING,
    PROPERTY_COMMERCIAL,
    ROLE_ADMIN,
    ROLE_JANITOR,
    ROLE_OWNER,
    ROLE_PRESIDENT,
    VOTE_ABSTAIN,
    VOTE_AGAINST,
    VOTE_FOR,
    VOTE_NONE,
    ALLOWED_FILE_TYPES,
    MAX_FILE_SIZE,
} from './constants'
import { navigate } from 'gatsby'
import { markAsSeen } from './firestore-utils'
import {
    AdminPanelSettingsRounded,
    KeyRounded,
    PeopleAltRounded,
    QuestionMarkRounded,
    StarRounded,
} from '@mui/icons-material'

dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.locale('es')

export const humanizeMinutes = (mins) => {
    if (mins < 59) {
        return `${mins} minuto${mins > 1 ? 's' : ''}`
    } else if (mins < 1440) {
        const hours = Math.floor(mins / 60)
        const remaining = mins % 60
        const hoursStr = `${hours} hora${hours > 1 ? 's' : ''}`
        const minsStr = remaining
            ? ` y ${remaining} minuto${remaining > 1 ? 's' : ''}`
            : ''
        return hoursStr + minsStr
    } else {
        const days = Math.floor(mins / 1440)
        const hours = Math.floor((mins % 1440) / 60)
        const remaining = (mins % 1440) % 60
        const daysStr = `${days} día${days > 1 ? 's' : ''}`
        const hoursStr = hours ? `, ${hours} hora${hours > 1 ? 's' : ''}` : ''
        const minsStr = remaining
            ? ` y ${remaining} minuto${remaining > 1 ? 's' : ''}`
            : ''
        return daysStr + hoursStr + minsStr
    }
}

export const humanizeDays = (days) => {
    return dayjs
        .duration(days, 'days')
        .locale('es')
        .humanize()
        .replace('un', '1')
}

export const humanizeDate = (date) => {
    return dayjs(date).format('LLL')
}

export const humanizeDuration = (now, since, beforeNow = false) => {
    return dayjs
        .duration(now.diff(since) * (beforeNow ? -1 : 1))
        .locale('es')
        .humanize(true)
}

export const getHumanizedNeighbourRoles = (neigh) => {
    if (!neigh) {
        return ''
    }
    return neigh.roles.map((r) => humanizeRole(r)).join(', ')
}

export const isMeetingOpenToVote = (meeting) => {
    const now = dayjs()
    const open = dayjs(meeting?.openVotesDate.toDate())
    return now.isAfter(open)
}

export const isMeetingClosedToVote = (meeting) => {
    const now = dayjs()
    const close = dayjs(meeting?.closeVotesDate.toDate())
    return now.isAfter(close)
}

export const humanizeSenseOfVote = (sense) => {
    switch (sense) {
        case VOTE_FOR:
            return 'A favor'
        case VOTE_AGAINST:
            return 'En Contra'
        case VOTE_ABSTAIN:
            return 'Abstención'
        case VOTE_NONE:
            return 'Sin votar'
        default:
            return 'Desconocido'
    }
}

export const humanizeRole = (role) => {
    switch (role) {
        case ROLE_ADMIN:
            return 'Administrador'
        case ROLE_JANITOR:
            return 'Trabajador'
        case ROLE_OWNER:
            return 'Propietario'
        case ROLE_PRESIDENT:
            return 'Presidente o Miembro de la Junta de Gobierno'
        default:
            return 'Desconocido'
    }
}

export const humanizePropertyType = (type, plural) => {
    switch (type) {
        case PROPERTY_HOME:
            return `Vivienda${plural ? 's' : ''}`
        case PROPERTY_PARKING:
            return `Garaje${plural ? 's' : ''}`
        case PROPERTY_COMMERCIAL:
            return `Local${plural ? 'es' : ''}`
        default:
            return 'Desconocido'
    }
}

export const getMultiplierFromSense = (sense) => {
    switch (sense) {
        case VOTE_FOR:
            return 1
        case VOTE_AGAINST:
            return -1
        case VOTE_ABSTAIN:
            return 0
        case VOTE_NONE:
            return 0
        default:
            return 0
    }
}

export const getAvatarColorFromLetter = (letter) => {
    if (!letter || !letter.length) {
        return AVATAR_COLORS[0]
    }
    const code = letter.charCodeAt(0)
    const idx = code % AVATAR_COLORS.length
    return AVATAR_COLORS[idx]
}

export const getColorFromSense = (sense) => {
    switch (sense) {
        case VOTE_FOR:
            return 'success'
        case VOTE_AGAINST:
            return 'secondary'
        case VOTE_ABSTAIN:
            return 'gray'
        case VOTE_NONE:
            return 'primary'
        default:
            return 'gray'
    }
}

export const goToMeeting = (neighRef, meeting) => {
    markAsSeen(neighRef, meeting.ref)
    navigate(`/neighbour/dashboard/meetings/${meeting.id}/`)
}

export const goToAnnounce = (neighRef, announce) => {
    markAsSeen(neighRef, announce.ref)
    navigate(`/neighbour/dashboard/announces/${announce.id}/`)
}

export const goToEditAnnounce = (neighRef, announce) => {
    markAsSeen(neighRef, announce.ref)
    navigate(`/admin/announce/edit/${announce.id}/`)
}

export const neighbourHasRoles = (neighbourData, roles) => {
    if (neighbourData && neighbourData.roles) {
        return neighbourData.roles.some((r) => roles.includes(r))
    }
    return false
}

export const getTicketDestinations = (neighbourData) => {
    const dests = {}
    if (!neighbourData) {
        return
    }

    if (neighbourHasRoles(neighbourData, [ROLE_ADMIN])) {
        dests[ROLE_JANITOR] = 'Trabajadores'
        dests[ROLE_PRESIDENT] = 'Presidente o Junta de Gobierno'
        dests['NONE'] = 'Vecinos'
    }
    if (neighbourHasRoles(neighbourData, [ROLE_OWNER])) {
        dests[ROLE_ADMIN] = 'Administrador'
        dests[ROLE_PRESIDENT] = 'Presidente o Junta de Gobierno'
    }
    if (neighbourHasRoles(neighbourData, [ROLE_JANITOR])) {
        dests[ROLE_ADMIN] = 'Administrador'
        dests[ROLE_PRESIDENT] = 'Presidente o Junta de Gobierno'
    }
    if (neighbourHasRoles(neighbourData, [ROLE_PRESIDENT])) {
        dests[ROLE_ADMIN] = 'Administrador'
        dests[ROLE_PRESIDENT] = 'Resto de miembros de la Junta de Gobierno'
        dests[ROLE_JANITOR] = 'Trabajadores'
        dests['NONE'] = 'Vecinos'
    }

    return dests
}

export const getAvailableDeliveryDestinations = (propertiesData) => {
    const availDest = {}
    propertiesData?.forEach((p) => {
        availDest[p.name] = { propRef: p.ref, neighRef: p.neighbourRef }
    })

    return availDest
}

export const appendIsNew = (items, neighbourData) => {
    if (neighbourData) {
        return items.map((d) => appendIsNewToItem(d, neighbourData))
    }
    return items
}

export const appendIsNewToItem = (item, neighbourData) => {
    if (item && neighbourData) {
        return {
            ...item,
            isNew:
                neighbourData.unseen.find((el) => el.id === item.id) !==
                undefined,
        }
    }
    return item
}

export const validateFile = (value) => {
    if (!value) {
        return undefined
    }

    if (!ALLOWED_FILE_TYPES.includes(value.type)) {
        return `El tipo de archivo no es válido. Solo se permiten JPEG, PNG y PDF. Actual: ${value.type}`
    }

    if (value.size > MAX_FILE_SIZE) {
        return 'El tamaño del archivo excede los 5MB.'
    }

    return undefined
}

export const getNeighbourIcon = (neigh, styles) => {
    if (neigh.roles.includes(ROLE_ADMIN)) {
        return <AdminPanelSettingsRounded sx={styles} />
    }
    if (neigh.roles.includes(ROLE_PRESIDENT)) {
        return <StarRounded sx={styles} />
    }
    if (neigh.roles.includes(ROLE_OWNER)) {
        return <PeopleAltRounded sx={styles} />
    }
    if (neigh.roles.includes(ROLE_JANITOR)) {
        return <KeyRounded sx={styles} />
    }

    return <QuestionMarkRounded sx={styles} />
}

export const requestNotificationPermission = () => {
    if (!('Notification' in window)) {
        console.log('Browser does not support desktop notification')
    } else {
        Notification.requestPermission()
    }
}

export const showNotification = (title, body) => {
    var options = {
        body,
        icon: 'https://conviveapp.es/static/icon-c0508be7cd187f6050a16020e8a78331.png',
    }
    new Notification(title, options)
}

export const sortPropertiesByTypeFcn = (a, b) => {
    if (a.type === b.type) {
        return a.name > b.name ? 1 : -1
    } else if (a.type === PROPERTY_HOME) {
        return -1
    } else if (b.type === PROPERTY_HOME) {
        return 1
    } else if (a.type === PROPERTY_COMMERCIAL) {
        return -1
    } else if (b.type === PROPERTY_COMMERCIAL) {
        return 1
    } else {
        return a.name > b.name ? 1 : -1
    }
}
